import type { AudienceSlug } from "@/app/(marketing)/experts/[audience]/_config/audiences";

// Single source of truth for how a landing audience is encoded as a signup
// source. The same string flows through:
//   1. cookie ; set by LandingTracker via setSignupSourceCookie()
//   2. DB    ; auth.ts user.create.after persists the cookie value to
//                users.signupSource (so `WHERE signupSource LIKE '<prefix>%'`
//                builds talent pools by audience).
//   3. event ; auth.ts captureServerEvent("signup_completed") parses the
//                audience back out via parseExpertsAudienceSource() for
//                PostHog funnel breakdowns.
// Adding a slug to AUDIENCE_SLUGS automatically extends this union; renaming
// the prefix breaks all three call sites in lockstep.
export const EXPERTS_AUDIENCE_SOURCE_PREFIX = "experts_audience_";

export type ExpertsAudienceSource =
  `${typeof EXPERTS_AUDIENCE_SOURCE_PREFIX}${AudienceSlug}`;

export type SignupSource =
  | "interview_login_prompt" // LoginPromptOverlay.tsx
  | "referral_link" // /refer/[token] page CTA
  | "recruitment_link" // RecruitmentAuthScreen.tsx
  | "login_page" // /login page direct
  | "researcher_login_page" // /researchers/login page direct
  | "join_page_split" // /join LinkedIn-only, split layout variant
  | "join_page_minimal" // /join LinkedIn-only, minimal centered variant (default)
  | ExpertsAudienceSource; // /experts/<audience> landing pages

export function toExpertsAudienceSource(
  slug: AudienceSlug,
): ExpertsAudienceSource {
  return `${EXPERTS_AUDIENCE_SOURCE_PREFIX}${slug}`;
}

export function parseExpertsAudienceSource(
  source: string | null | undefined,
): string | null {
  if (!source?.startsWith(EXPERTS_AUDIENCE_SOURCE_PREFIX)) {
    return null;
  }
  return source.slice(EXPERTS_AUDIENCE_SOURCE_PREFIX.length);
}
